import { activitiesConstants } from "./../constants/activities.constants";
import { CommonAcquisition } from '../types';
import { Rep } from "../classes/Workout";
import { ChartDataPoint } from '../services/ChartDataManager';

export interface ActivityFiltersState {
    title: string;
    type: string;
}

export interface ActivitiesState {
    requesting: boolean;
    received: boolean;
    activities: Array<CommonAcquisition>;
    total: number;
    page: number;
    uploadedId: any;
    filters: ActivityFiltersState;
    activityStatus: string;
    timerStatus:string;
    calibrationStatus: string;
    currentRep:Rep;
    
    // Timer state
    elapsedTime: number;
    
    // Chart data state (for display)
    chartData: ChartDataPoint[];
    chartMaxValue: number;
    chartMaxTime: number;
    
    // Sensor data state (for backend export)
    sensorData: [number, number][]; // [force, time] pairs for backend
    
    // Workout state management
    workoutState: {
        phase: 'idle' | 'ready' | 'active' | 'paused' | 'completed';
        repPhase: 'wait_start' | 'hang' | 'rest' | 'wait_hang' | 'wait_rest';
        currentRepIndex: number;
        totalReps: number;
        isInitialized: boolean;
        isBluetoothReady: boolean;
    };
    
    // Rep timer state
    repTimer: {
        isRunning: boolean;
        elapsedTime: number;
        startTime: number;
    };
    
    // Global timer state
    globalTimer: {
        isRunning: boolean;
        elapsedTime: number;
        startTime: number;
    };
    
    // Current strength display
    currentStrength: number | 'MAX' | 'Out';
    
    // Workout-wide chart data (all reps combined)
    workoutChartData: ChartDataPoint[];
}

const defaultState: ActivitiesState = {
    requesting: false,
    received: false,
    activities: [],
    uploadedId: null,
    total: 0,
    page: 1,
    filters: { title: "", type: "" },
    activityStatus: 'calibrate', // Start with calibrate instead of stop
    timerStatus:'stop',
    calibrationStatus: 'stop',
    currentRep:null,
    
    // Timer state
    elapsedTime: 0,
    
    // Chart data state (for display)
    chartData: [],
    chartMaxValue: 0,
    chartMaxTime: 0,
    
    // Sensor data state (for backend export)
    sensorData: [],
    
    // Workout state management
    workoutState: {
        phase: 'idle' as const,
        repPhase: 'wait_start' as const,
        currentRepIndex: 0,
        totalReps: 0,
        isInitialized: false,
        isBluetoothReady: false,
    },
    
    // Rep timer state
    repTimer: {
        isRunning: false,
        elapsedTime: 0,
        startTime: 0,
    },
    
    // Global timer state
    globalTimer: {
        isRunning: false,
        elapsedTime: 0,
        startTime: 0,
    },
    
    // Current strength display
    currentStrength: 0,
    
    // Workout-wide chart data (all reps combined)
    workoutChartData: []
};

export const activities = (state: ActivitiesState = defaultState, action: any) => {
    switch (action.type) {
        case activitiesConstants.ACTIVITIES_REQUEST:
            return { ...state, requesting: true, received: false };
        case activitiesConstants.ACTIVITIES_SUCCESS:
            return {
                ...state,
                requesting: false,
                received: true,
                activities: action.data.activities,
                total: action.data.total
            };
        case activitiesConstants.ACTIVITIES_FAILURE:
            return { ...defaultState };
        case activitiesConstants.ARCHIVE_SUCCESS:
            const act = state.activities;
            const index = act.map((a) => {return a.id; }).indexOf(action.data);
            act.splice(index, 1);
            return { ...state, activities: act, total: state.total - 1};
        case activitiesConstants.ARCHIVE_FAILURE:
            return { ...state };
        case activitiesConstants.UPDATE_FILTERS:
            return { ...state, filters: action.data };
        case activitiesConstants.SET_PAGE:
            return { ...state, page: action.data };
        case activitiesConstants.ACTIVITIES_RESET_STATE:
            return { ...defaultState };
        case activitiesConstants.UPLOAD_SUCCESS:
            return {
                ...state,
                uploadedId: action.data.id,
            };
        case activitiesConstants.UPLOAD_FAILURE:
            return { ...defaultState };
        case activitiesConstants.ACTIVITY_STATUS:
            return{...state, activityStatus: action.data};
        case activitiesConstants.TIMER_STATUS:
            return { ...state, timerStatus: action.data };
        case activitiesConstants.CALIBRATION_SATUS:
            return { ...state, calibrationStatus: action.data };
        case activitiesConstants.CURRENT_REP:
            return { ...state, currentRep: action.data };
            
        // Timer actions
        case activitiesConstants.UPDATE_ELAPSED_TIME:
            return { ...state, elapsedTime: action.data };
        case activitiesConstants.RESET_ELAPSED_TIME:
            return { ...state, elapsedTime: 0 };
            
        // Chart data actions
        case activitiesConstants.UPDATE_CHART_DATA:
            return { ...state, chartData: action.data };
        case activitiesConstants.ADD_CHART_DATA_POINT: {
            const newChartData = [...state.chartData, action.data];
            const newMaxValue = Math.max(state.chartMaxValue, action.data.y);
            const newMaxTime = Math.max(state.chartMaxTime, action.data.x);
            return { ...state, chartData: newChartData, chartMaxValue: newMaxValue, chartMaxTime: newMaxTime };
        }
        case activitiesConstants.ADD_CHART_DATA_POINTS: {
            if (!action.data || action.data.length === 0) return state;
            const batch: { x: number; y: number }[] = action.data;
            const newChartData = [...state.chartData, ...batch];
            const batchMaxY = batch.reduce((m, p) => Math.max(m, p.y), state.chartMaxValue);
            const batchMaxX = batch.reduce((m, p) => Math.max(m, p.x), state.chartMaxTime);
            return { ...state, chartData: newChartData, chartMaxValue: batchMaxY, chartMaxTime: batchMaxX };
        }
        case activitiesConstants.CLEAR_CHART_DATA:
            return { ...state, chartData: [], chartMaxValue: 0, chartMaxTime: 0 };
        case activitiesConstants.UPDATE_CHART_MAX_VALUES:
            return { 
                ...state, 
                chartMaxValue: action.data.maxValue, 
                chartMaxTime: action.data.maxTime 
            };
            
        // Sensor data actions
        case activitiesConstants.ADD_SENSOR_DATA_POINT:
            return { ...state, sensorData: [...state.sensorData, action.data] };
        case activitiesConstants.CLEAR_SENSOR_DATA:
            return { ...state, sensorData: [] };
            
        // Workout state management
        case activitiesConstants.INITIALIZE_WORKOUT:
            return { 
                ...state, 
                workoutState: {
                    ...state.workoutState,
                    ...action.data,
                    isInitialized: true
                }
            };
        case activitiesConstants.START_WORKOUT:
            return { 
                ...state, 
                workoutState: {
                    ...state.workoutState,
                    phase: 'active',
                    repPhase: 'hang'
                }
            };
        case activitiesConstants.PAUSE_WORKOUT:
            return { 
                ...state, 
                workoutState: {
                    ...state.workoutState,
                    phase: 'paused'
                }
            };
        case activitiesConstants.RESUME_WORKOUT:
            return { 
                ...state, 
                workoutState: {
                    ...state.workoutState,
                    phase: 'active'
                }
            };
        case activitiesConstants.COMPLETE_REP:
            const nextRepIndex = state.workoutState.currentRepIndex + 1;
            const isLastRep = nextRepIndex >= state.workoutState.totalReps;
            return { 
                ...state, 
                workoutState: {
                    ...state.workoutState,
                    currentRepIndex: nextRepIndex,
                    repPhase: isLastRep ? 'wait_start' : (action.data?.intensity > 0 ? 'hang' : 'rest'),
                    phase: isLastRep ? 'completed' : 'active'
                }
            };
        case activitiesConstants.COMPLETE_WORKOUT:
            return { 
                ...state, 
                workoutState: {
                    ...state.workoutState,
                    phase: 'completed',
                    repPhase: 'wait_start'
                }
            };
        case activitiesConstants.RESET_WORKOUT:
            return { 
                ...state, 
                workoutState: {
                    ...state.workoutState,
                    phase: 'idle',
                    repPhase: 'wait_start',
                    currentRepIndex: 0
                }
            };
            
        // Rep timer management
        case activitiesConstants.START_REP_TIMER:
            return { 
                ...state, 
                repTimer: {
                    isRunning: true,
                    elapsedTime: 0,
                    startTime: Date.now()
                }
            };
        case activitiesConstants.STOP_REP_TIMER:
            return { 
                ...state, 
                repTimer: {
                    ...state.repTimer,
                    isRunning: false
                }
            };
        case activitiesConstants.RESET_REP_TIMER:
            return { 
                ...state, 
                repTimer: {
                    isRunning: false,
                    elapsedTime: 0,
                    startTime: 0
                }
            };
        case activitiesConstants.UPDATE_REP_ELAPSED_TIME:
            return { 
                ...state, 
                repTimer: {
                    ...state.repTimer,
                    elapsedTime: action.data
                }
            };
            
        // Global timer management
        case activitiesConstants.START_GLOBAL_TIMER:
            return { 
                ...state, 
                globalTimer: {
                    isRunning: true,
                    elapsedTime: 0,
                    startTime: Date.now()
                }
            };
        case activitiesConstants.STOP_GLOBAL_TIMER:
            return { 
                ...state, 
                globalTimer: {
                    ...state.globalTimer,
                    isRunning: false
                }
            };
        case activitiesConstants.RESET_GLOBAL_TIMER:
            return { 
                ...state, 
                globalTimer: {
                    isRunning: false,
                    elapsedTime: 0,
                    startTime: 0
                }
            };
        case activitiesConstants.UPDATE_GLOBAL_ELAPSED_TIME:
            return { 
                ...state, 
                globalTimer: {
                    ...state.globalTimer,
                    elapsedTime: action.data
                }
            };
            
        // Current strength display
        case activitiesConstants.UPDATE_CURRENT_STRENGTH:
            return { ...state, currentStrength: action.data };
            
        // Workout-wide chart data
        case activitiesConstants.ADD_WORKOUT_CHART_DATA_POINT:
            return { ...state, workoutChartData: [...state.workoutChartData, action.data] };
        case activitiesConstants.CLEAR_WORKOUT_CHART_DATA:
            return { ...state, workoutChartData: [] };
            
        default:
            return { ...state };
    }
};